import { ConversationFilterEnum, ConversationPriorityEnum } from "common/enums";
import { Conversation, Message } from "common/types";
import { filter } from "lodash";
import orderBy from "lodash/orderBy";
import { create } from "zustand";

export type ChatStore = {
  conversations: Conversation[];
  selectedConversation: Conversation;
  openedChatMessages: Message[];
  setOpenedChatMessages: (messages: Message[]) => void;
  setConversations: (conversations: Conversation[]) => void;
  setSelectedConversation: (conversation: Conversation) => void;
  closeConversation(): void;
  conversationFilter: ConversationFilterEnum;
  setConversationFilter: (filter: ConversationFilterEnum) => void;
  conversationsCount: Record<ConversationFilterEnum, number>;
  setConversationsCount: (
    count: Record<ConversationFilterEnum, number>
  ) => void;
};

export const useChatStore = create<ChatStore>(
  (set, get): ChatStore => ({
    conversationFilter: ConversationFilterEnum.ALL,
    conversations: [],
    conversationsCount: {
      [ConversationFilterEnum.ALL]: 0,
      [ConversationFilterEnum.HIGH]: 0,
      [ConversationFilterEnum.MEDIUM]: 0,
      [ConversationFilterEnum.LOW]: 0,
      [ConversationFilterEnum.NO_PRIORITY]: 0,
      [ConversationFilterEnum.GROUP]: 0,
    },
    selectedConversation: null,
    setConversationFilter: (filter: ConversationFilterEnum) => {
      set({ conversationFilter: filter });
    },
    setConversations: (newConversations: Conversation[]) => {
      if (newConversations?.length < 0) {
        set({ conversations: [] });
        return;
      }

      const allConversations = orderBy(
        newConversations,
        [(conversation) => new Date(conversation?.messages[0]?.receivedAt)],
        ["desc"]
      );

      if (get().conversationFilter === ConversationFilterEnum.ALL) {
        set({
          conversations: [
            ...filter(allConversations, [
              "priority",
              ConversationPriorityEnum.HIGH,
            ]),
            ...filter(allConversations, [
              "priority",
              ConversationPriorityEnum.MEDIUM,
            ]),
            ...filter(allConversations, [
              "priority",
              ConversationPriorityEnum.LOW,
            ]),
          ],
        });
      } else {
        set({ conversations: allConversations });
      }

      /**
       * Ensures the selected conversation stays updated and remains open even if the search yields no results
       */
      const selectedConversationData = newConversations.filter(
        (conversation) => conversation?.id === get().selectedConversation?.id
      )[0];

      if (get().selectedConversation?.id && selectedConversationData) {
        set({
          selectedConversation: selectedConversationData,
        });
      }
    },
    setConversationsCount: (
      _conversationsCount: Record<ConversationFilterEnum, number>
    ) => {
      set({
        conversationsCount: {
          ..._conversationsCount,
          [ConversationFilterEnum.ALL]:
            _conversationsCount.HIGH +
            _conversationsCount.MEDIUM +
            _conversationsCount.LOW,
        },
      });
    },
    setSelectedConversation: (selectedConversation: Conversation) => {
      set({ selectedConversation });
    },
    openedChatMessages: [],
    setOpenedChatMessages: (messages: Message[]) => {
      set({ openedChatMessages: messages });
    },
    closeConversation: () =>
      set({ selectedConversation: null, openedChatMessages: [] }),
  })
);
